import { EnvironmentProviders, InjectionToken, makeEnvironmentProviders } from '@angular/core';
import { MediaFitMode, MediaSort, MediaTheme, MediaViewStyle } from './models/media.model';

/**
 * Application-wide defaults for every `<app-media-viewer>`. A component input always wins over the
 * global value, which in turn wins over {@link DEFAULT_MEDIA_VIEWER_CONFIG}.
 */
export interface MediaViewerConfig {
  /** Initial gallery layout. */
  view: MediaViewStyle;
  /** Initial sort order applied to the gallery. */
  sort: MediaSort;
  theme: MediaTheme;
  /** Image fit mode used when the lightbox opens. */
  fitMode: MediaFitMode;
  /** Initial zoom as a percentage (100 = actual size). */
  defaultZoom: number;
  minZoom: number;
  maxZoom: number;
  /** Multiplicative step applied by the zoom in/out buttons and wheel. */
  zoomStep: number;
  /** Items shown per page in the gallery; `0` disables paging (show everything). */
  pageSize: number;
  showToolbar: boolean;
  showSearch: boolean;
  showFilter: boolean;
  showSort: boolean;
  showViewToggle: boolean;
  showUpload: boolean;
  showRefresh: boolean;
  showZoom: boolean;
  showRotate: boolean;
  showDownload: boolean;
  showPrint: boolean;
  showFullscreen: boolean;
  showThumbnails: boolean;
  /** Show the light/dark theme toggle in the lightbox. Off by default. */
  showTheme: boolean;
  enableKeyboardNavigation: boolean;
  enableWheelZoom: boolean;
  enableSwipe: boolean;
}

export const DEFAULT_MEDIA_VIEWER_CONFIG: MediaViewerConfig = {
  view: 'grid',
  sort: { field: 'date', dir: 'desc' },
  theme: 'light',
  fitMode: 'contain',
  defaultZoom: 100,
  minZoom: 25,
  maxZoom: 500,
  zoomStep: 1.25,
  pageSize: 24,
  showToolbar: true,
  showSearch: true,
  showFilter: true,
  showSort: true,
  showViewToggle: true,
  showUpload: true,
  showRefresh: true,
  showZoom: true,
  showRotate: true,
  showDownload: false,
  showPrint: true,
  showFullscreen: true,
  showThumbnails: true,
  showTheme: false,
  enableKeyboardNavigation: true,
  enableWheelZoom: true,
  enableSwipe: true,
};

/** DI token holding the resolved {@link MediaViewerConfig}. Optional — the component falls back to defaults. */
export const MEDIA_VIEWER_CONFIG = new InjectionToken<MediaViewerConfig>('MEDIA_VIEWER_CONFIG');

/**
 * Register global media-viewer defaults at the application (or route) level.
 *
 * ```ts
 * // app.config.ts
 * providers: [
 *   provideMediaViewer({ view: 'grid', minZoom: 25, maxZoom: 500, theme: 'light' }),
 * ];
 * ```
 */
export function provideMediaViewer(config: Partial<MediaViewerConfig> = {}): EnvironmentProviders {
  return makeEnvironmentProviders([
    { provide: MEDIA_VIEWER_CONFIG, useValue: { ...DEFAULT_MEDIA_VIEWER_CONFIG, ...config } },
  ]);
}
